import { DashboardTabsApi } from '@/entities/dashboards/dashboard-tab';
import {
  appendUrlVariableValues,
  type DashboardVariableValues,
} from '@/entities/dashboards/dashboard';
import type { AdvancedTQLFilter } from '@/entities/srm/tql-query';
import { FEATURE_FLAGS } from '@/shared/config';
import {
  OVERVIEW,
  HOME,
  REDIRECT,
  SETTINGS_MONITORING_TEMPLATES,
} from '@/shared/config/routes';
import { useUserRules } from '@/entities/user/access-rule';
import { InterceptedError } from '@/shared/api/intercepted-error';
import { useDidUpdate } from '@mantine/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { generatePath, useNavigate } from 'react-router-dom';

export type RedirectPreset = (
  params: Record<string, string>,
  state?: unknown,
) => {
  isLoading: boolean;
  redirectUrl: string | null;
  isError: boolean;
  error?: InterceptedError;
  message: string;
  state?: unknown;
};

interface Presets extends Record<string, RedirectPreset> {}

interface OpenDashboardState {
  placeAfterTabId?: string;
  advanced_tql_params?: AdvancedTQLFilter;
  variable_values?: DashboardVariableValues;
}

export const useRedirect = (type: keyof typeof redirectPresets) => {
  const navigate = useNavigate();

  return (params?: Record<string, string>, state?: unknown) => {
    const urlParams = new URLSearchParams(params || {}).toString();
    const url = `${generatePath(REDIRECT, { 'redirect-type': String(type) })}${urlParams ? `?${urlParams}` : ''}`;

    navigate(url, { state });
  };
};

// TODO(auth-flow): Consider a component registry for all redirect scenarios,
// including login preparation/session restoration and logout. First separate
// authentication/logout operations from navigation and support loading, retry,
// and final redirects here. Preserve next and exclude auth redirects from the
// prefetch loader to avoid loops. Reuse prepareApplication in both the login
// redirect and modal reauthentication; the latter must not navigate or unmount
// the current page. Keep the current architecture until that migration.
export const redirectPresets: Presets = {
  ['open-dashboard']: (params, externalState) => {
    const name = params?.name;
    const openState = externalState as OpenDashboardState | null;
    const advancedTqlParams = openState?.advanced_tql_params;
    const variableValues = openState?.variable_values;

    const { isLoading, isError, error, data } = useQuery({
      queryKey: ['redirecting', name, openState],
      queryFn: async () => {
        const tab = await DashboardTabsApi.createDashboardTab(name, {
          title: null,
        }).then((res) => res.data);

        if (advancedTqlParams) {
          if (!tab.config) throw new Error('Created tab has no config');
          await DashboardTabsApi.updateTab({
            id: tab.id,
            config: {
              ...tab.config,
              filter: advancedTqlParams,
            },
          });
        }

        return tab;
      },
      cacheTime: 0,
      enabled: !!name,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    const redirectParams = new URLSearchParams();
    if (data?.id) redirectParams.set('tab', data.id);
    if (variableValues && FEATURE_FLAGS.dashboardVariables) {
      appendUrlVariableValues(redirectParams, variableValues);
    }
    const redirectUrl =
      name && data?.id ? `${OVERVIEW}?${redirectParams.toString()}` : null;

    return {
      isLoading,
      redirectUrl,
      isError: !name || isError,
      error: error as InterceptedError,
      message: 'Redirecting...',
      state: externalState, // ✅ Use the passed state
    };
  },
  ['access-reset']: () => {
    const queryClient = useQueryClient();
    const { isLoading, isError, error } = useUserRules();

    useDidUpdate(() => {
      queryClient.resetQueries(['user-rules', true]);
    }, []);

    const redirectUrl = HOME;

    return {
      isLoading,
      redirectUrl,
      isError: isError,
      error: error as InterceptedError,
      message: 'Redirecting...',
    };
  },
  ['open-monitoring-template']: (params) => {
    const redirectUrl = `${SETTINGS_MONITORING_TEMPLATES}?selected=${encodeURIComponent(params?.templateName ?? '')}`;
    return {
      isLoading: false,
      redirectUrl,
      isError: false,
      message: 'Redirecting...',
    };
  },
};